// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { createContext } from 'preact';
import { useState, useCallback, useContext, useRef, useEffect } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { NetworkName, KeyAgentClient } from './key-agent-loader.js';
import { createKeyAgentClient } from './key-agent-loader.js';
import type { AppSignatureProof } from '@cobault/key-agent';
import {
  api,
  ApiError,
  isOfferUnavailable,
  setAuthToken,
  setRefreshToken,
  setAuthTokenExpiresAt,
  setOnRefresh,
  setOnUnauthorized,
  setOnGeoRestricted,
  GeoRestrictedError,
  type GeoTier,
  type OfferResult,
  type PasscodeCheckStatus,
} from './api.js';
import { BootstrapUnreachable } from './components/bootstrap-unreachable.js';
import { GeoRestricted } from './components/geo-restricted.js';

/** COB-586 — the shared-vault claim-link landing, mirrored from the
 *  `/shared-vault/accept` route in `app.tsx`. Kept as a constant so the
 *  geo gate's invite-aware copy and the route it keys off cannot drift
 *  apart silently. */
const ACCEPT_PATH = '/shared-vault/accept';

// COB-385: single 5-minute inactivity window. The SPA idle clock and
// the key-agent wallet lock share this value so the wallet can never
// silently lock while the session still looks alive (the pre-COB-385
// failure mode: a 2-min wallet lock under a 3-min idle window). The
// access token is decoupled — JWT_TTL_SECS is 900 with proactive
// refresh (api.ts), so the idle clock alone is the user-facing
// session boundary; on expiry the client discards its tokens.
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
// The key-agent inactivity lock, passed as `timeoutMs` at every
// long-lived agent construction (software + Web3Auth; hardware has no
// browser-held key to lock). Same window as the idle clock by design —
// change them together or the silent-lock failure mode returns.
const WALLET_LOCK_TIMEOUT_MS = IDLE_TIMEOUT_MS;
// COB-385: events that count as deliberate input for the wallet-lock
// clock. Mousemove/scroll keep the idle session alive but must not
// keep key material unlocked — only intentful input does.
const INTENTFUL_ACTIVITY_EVENTS: ReadonlySet<string> = new Set([
  'keydown',
  'click',
  'touchstart',
]);
// How close to expiry the countdown chip starts showing.
const COUNTDOWN_THRESHOLD_MS = 60 * 1000;
// Tick cadence for the idle clock. 1s is the granularity the
// countdown UI needs; faster would just burn cycles on background
// tabs without changing what the user sees.
const IDLE_TICK_MS = 1000;

/** Map the backend's `bitcoin_network` string to the key agent's
 *  NetworkName. Unknown values fail closed to mainnet so we never
 *  accidentally derive testnet addresses on a real-money deployment.
 *  Exported for the HW.4 registration ceremony (pages/accept.tsx), which
 *  resolves the emulated co-signers' network the same way the session
 *  resolves the agent's. */
export function toKeyAgentNetwork(bitcoinNetwork: string): NetworkName {
  switch (bitcoinNetwork) {
    case 'bitcoin':
      return 'mainnet';
    case 'signet':
    case 'testnet':
      return bitcoinNetwork as NetworkName;
    case 'regtest':
      return 'regtest';
    default:
      return 'mainnet';
  }
}

/** COB-559: the value `session.bitcoinNetwork` takes when `GET /version`
 *  never answers — i.e. "we do not know the network," not "assume
 *  mainnet." Every UI consumer of `bitcoinNetwork` (mempoolAddressUrlForNetwork,
 *  verifierOriginForNetwork, verifierVaultUrlForNetwork, allowlistAddError's
 *  mismatch hint, ...) already treats `null` as "suppress the link/hint
 *  rather than guess" — the bootstrap catch handler used to defeat that by
 *  passing the literal string `'bitcoin'` instead, which painted clickable
 *  mainnet mempool.space links (and other mainnet-branded affordances) on
 *  a page that might really be on signet/testnet/regtest. This is
 *  deliberately the same `null` the state is initialised to, never a fake
 *  network string — do not change this back to `'bitcoin'`.
 *
 *  Not used for `toKeyAgentNetwork`'s input: that fallback (see
 *  `versionPromiseRef` below) answers a different question — "what network
 *  must a key agent be pinned to right now" has no `null` answer, so it
 *  intentionally poisons itself toward mainnet + a zero salt so the
 *  backend rejects the derivation outright rather than silently accepting
 *  a wrong-network one. Display state and key-agent construction must not
 *  share a fallback. */
export const UNKNOWN_BITCOIN_NETWORK: string | null = null;

/**
 * F12/F8: single source of truth for the session lifecycle.
 *
 * - `unauthenticated` — no agent, no JWT. The default state; also the
 *   state after explicit logout or a 401 has torn everything down.
 * - `authenticated`  — agent is unlocked, seed is present, JWT valid.
 *   This is the only state from which protected requests should be
 *   made. Vault lists, offer fetches, wallet endpoints all gate on
 *   this.
 * - `locked`         — agent exists and has a seed but the passphrase
 *   is no longer in memory (inactivity timeout or explicit lock). The
 *   JWT may still be valid but the user cannot sign anything until
 *   they re-enter the passphrase.
 * - `expired`        — the JWT has expired or been rejected with 401.
 *   The agent may still be unlocked; the user can re-auth without
 *   re-entering the passphrase by going through the onboarding flow
 *   again (or explicit logout).
 */
/** Thrown by `loginWithWeb3Auth` when the social-login email already
 *  belongs to an existing account. Email is the one-account-per-human
 *  anchor, and a web3auth key is a different key (different identity
 *  pubkey) — so this isn't a login, it's a collision. The SPA catches
 *  this to bounce the user to the Self Custody login with the email
 *  prefilled, rather than surfacing a raw 409.
 *
 *  COB-345: the copy is method-neutral. The server's 409 body cannot say
 *  whether the holder is a passcode or a web3auth account (no
 *  machine-readable code; the SPA is barred from string-matching the
 *  message), so the message must not assert "passcode". The bounce still
 *  routes to the Self Custody panel — where a passcode holder can sign in
 *  — but the copy no longer claims that is the only possibility. */
export class EmailHasLocalAccountError extends Error {
  constructor(public readonly email: string) {
    super(
      'An account with this email already exists. Sign in with the method you ' +
        'used when you created it.',
    );
    this.name = 'EmailHasLocalAccountError';
  }
}

/** COB-345: thrown by `loginWithWeb3Auth` when a Web3Auth sign-in yields a
 *  key but NO email — the pubkey was unknown (a brand-new account), so
 *  there is nothing to register under. The chooser catches this to route to
 *  the email-capture panel; the live agent is stashed in
 *  `pendingWeb3AuthAgentRef` so `completeWeb3AuthEmailRegistration` can
 *  finish the registration with a typed email. Carries no email (that is
 *  precisely what is missing). */
export class Web3AuthEmailRequiredError extends Error {
  constructor() {
    super(
      "This sign-in didn't share an email address. Enter one to finish setting " +
        'up your Cobault account.',
    );
    this.name = 'Web3AuthEmailRequiredError';
  }
}

/** COB-272 (FIND-009): thrown by `loginWithWeb3Auth` when a brand-new
 *  account is ready to register under a provider email, BEFORE the register
 *  fires. Losing the social login means losing the signing key it protects,
 *  so registration is gated on an explicit acknowledgement: the chooser
 *  catches this to route to the loss-acknowledgement panel; the live agent
 *  and register parameters are stashed so `completeWeb3AuthRegistration`
 *  can finish once the user has acknowledged. */
export class Web3AuthLossAckRequiredError extends Error {
  constructor() {
    super(
      'Acknowledge that losing your login means losing this signing key ' +
        'to finish creating your Cobault account.',
    );
    this.name = 'Web3AuthLossAckRequiredError';
  }
}

/** COB-345: thrown by `completeWeb3AuthEmailRegistration` when the typed
 *  capture email already belongs to a different Cobault account (the
 *  one-account-per-email 409). Unlike {@link EmailHasLocalAccountError}
 *  this does NOT bounce to the passcode panel — the user is mid-Web3Auth
 *  and a passcode account is only one of several possibilities — the
 *  capture panel shows it inline so they can try a different address. */
export class Web3AuthEmailTakenError extends Error {
  constructor(public readonly email: string) {
    super(
      'That email is already attached to another Cobault account. If it’s ' +
        'yours, sign in with the method you used when you created it.',
    );
    this.name = 'Web3AuthEmailTakenError';
  }
}

/** COB-345: the two shapes `doSiwbVerify` resolves to for a Web3Auth login
 *  — success (the pubkey was already known) or a failure carrying the
 *  thrown error for classification. */
export type Web3AuthVerifyOutcome =
  | { ok: true }
  | { ok: false; error: unknown };

/** COB-345: what `loginWithWeb3Auth` does after `doSiwbVerify`.
 *  - `authenticated`  — verify OK, provider email present: the returning
 *    user is in and `userEmail` was already committed. Nothing more.
 *  - `hydrate-email`  — verify OK, provider email absent: the returning
 *    user is in but the SDK withheld the email; read it off the status
 *    endpoint (the auth token is committed by now).
 *  - `register`       — `unknown_user` + provider email present: a brand-
 *    new account with an email in hand — the existing register branch.
 *  - `capture-email`  — `unknown_user` + NO provider email: a brand-new
 *    account with nothing to register under; prompt for one.
 *  - `rethrow`        — any other verify failure; surface unchanged. */
export type Web3AuthPostVerifyAction =
  | 'authenticated'
  | 'hydrate-email'
  | 'register'
  | 'capture-email'
  | 'rethrow';

/** COB-345: pure decision table for the post-verify branch of a Web3Auth
 *  login. The identity pubkey — not the email — resolves the account at
 *  verify, so a returning user never prompts regardless of whether the
 *  provider shared an email; only a genuinely new account (`unknown_user`)
 *  that ALSO lacks a provider email needs the capture step. Exported +
 *  side-effect-free so the whole table is unit-tested without a Worker or
 *  the Web3Auth SDK. */
export function decideWeb3AuthPostVerify(
  outcome: Web3AuthVerifyOutcome,
  hasProviderEmail: boolean,
): Web3AuthPostVerifyAction {
  if (outcome.ok) {
    return hasProviderEmail ? 'authenticated' : 'hydrate-email';
  }
  const err = outcome.error;
  if (err instanceof ApiError && err.status === 401 && err.message === 'unknown_user') {
    return hasProviderEmail ? 'register' : 'capture-email';
  }
  return 'rethrow';
}

/** COB-345: map a register failure from the email-capture step to the error
 *  the panel throws. A 409 means the typed email already has a verified
 *  holder — a different account — so it becomes {@link Web3AuthEmailTakenError}
 *  (shown inline; NO passcode bounce). Any other error passes through
 *  unchanged. Unlike the primary-login register branch (which maps 409 to
 *  {@link EmailHasLocalAccountError} and DOES bounce), the capture step never
 *  bounces — the user is mid-Web3Auth. Pure + exported so the mapping is unit-
 *  tested without a Worker. */
export function mapCaptureRegisterError(err: unknown, normalizedEmail: string): unknown {
  if (err instanceof ApiError && err.status === 409) {
    return new Web3AuthEmailTakenError(normalizedEmail);
  }
  return err;
}

/** COB-272: map a register failure on the primary (provider-email) Web3Auth
 *  branch to the error the caller throws. A 409 = the email already belongs
 *  to a different account, surfaced as {@link EmailHasLocalAccountError} so
 *  the chooser bounces to Self Custody with the email prefilled (unlike the
 *  capture step's inline {@link Web3AuthEmailTakenError} — see
 *  {@link mapCaptureRegisterError} for why the two differ). Any other error
 *  passes through unchanged. Pure + exported so the mapping is unit-tested
 *  without a Worker. */
export function mapPrimaryRegisterError(err: unknown, normalizedEmail: string): unknown {
  if (err instanceof ApiError && err.status === 409) {
    return new EmailHasLocalAccountError(normalizedEmail);
  }
  return err;
}

/** COB-346: thrown by `changeEmail` when the target address already
 *  belongs to a different Cobault account (the one-account-per-email
 *  409, disambiguated from "already verified" via the status endpoint —
 *  see {@link resolveChangeEmailConflict}). The banner shows this copy
 *  inline; the user picks a different address. */
export class ChangeEmailTakenError extends Error {
  constructor(public readonly email: string) {
    super('That email is already attached to another Cobault account.');
    this.name = 'ChangeEmailTakenError';
  }
}

/** COB-346: how `changeEmail` resolves an ambiguous 409 from
 *  `POST /auth/change-email`. The server returns 409 for BOTH "the
 *  target address is held verified by another account" AND "this account
 *  is already verified, so the change is out of scope" — and the SPA is
 *  barred from string-matching the error body to tell them apart. So on
 *  any 409 the caller re-reads its own verified status and this pure
 *  helper decides:
 *  - `self-heal`      — the account is now verified (it must have
 *    verified in another tab); drop the stale banner via
 *    `refreshEmailVerification` instead of showing an error.
 *  - `taken-by-other` — still unverified, so the 409 was the target
 *    address being taken; surface {@link ChangeEmailTakenError}.
 *  (The server's passcode-account 409 never reaches this decision: the
 *  affordance is hidden for software sessions via
 *  {@link changeEmailAvailable}, so a software session cannot issue the
 *  call from the banner in the first place.)
 *  Pure + exported so the decision is unit-tested without a network. */
export function resolveChangeEmailConflict(
  ownEmailVerified: boolean,
): 'self-heal' | 'taken-by-other' {
  return ownEmailVerified ? 'self-heal' : 'taken-by-other';
}

/** COB-346: whether the change-email affordance is available for this
 *  session. Passwordless sessions only ('web3auth' covers embedded
 *  Web3Auth; 'hardware' covers Ledger) — a passcode ('software')
 *  account's Argon2id verifier is derived from its email and the
 *  endpoint does not re-derive it, so the server refuses those with a
 *  409 and the banner must not offer what the server will refuse
 *  (passcode users keep the resend-only banner exactly as before).
 *  `null` (no agent kind committed yet) is unavailable too — never show
 *  an affordance the session might not be entitled to. Pure + exported
 *  so the visibility table is unit-tested without a render. */
export function changeEmailAvailable(agentKind: AgentKind | null): boolean {
  return agentKind === 'web3auth' || agentKind === 'hardware';
}

/** HW.3: thrown by `loginWithHardware` when first-time registration hits
 *  the one-account-per-email 409. Unlike the Web3Auth collision there is
 *  no bounce target — the email belongs to a different identity (another
 *  sign-in method or another device), and email cannot be re-bound at
 *  register. Since HW.6 the identity is device-native, so the same
 *  device logs into its account from any browser; a 409 here really
 *  does mean "different identity", never "same device, new browser".
 *  Surfaced verbatim by the hardware login panel. */
export class EmailAlreadyRegisteredError extends Error {
  constructor(public readonly email: string) {
    super(
      'This email already has a Cobault account (created with a different ' +
        'sign-in method or device). Use that sign-in method, or a ' +
        'different email for this device.',
    );
    this.name = 'EmailAlreadyRegisteredError';
  }
}

/** COB-336: thrown by `reconnectHardwareAgent` when the device connected on
 *  a retry is NOT the device the session was established with (its derived
 *  fingerprint differs). The reconnect is refused and the fresh connection
 *  torn down — a vault's keys live on one specific device, so a different
 *  Ledger must never be swapped into a session scoped to the original. */
export class HardwareDeviceMismatchError extends Error {
  constructor() {
    super(
      'A different device was connected. Reconnect the same Ledger you started ' +
        'this vault with, unlock it, then try again.',
    );
    this.name = 'HardwareDeviceMismatchError';
  }
}

/**
 * COB-336: connect a fresh hardware device and assert it is the SAME device
 * as the session's, returning its agent for the caller to swap in. Pure and
 * dependency-injected (the WebHID connect + fingerprint derivation are
 * passed in) so it is unit-testable with fake agents. `connect` MUST run
 * inside a user gesture (it may re-prompt WebHID). A fingerprint mismatch
 * throws {@link HardwareDeviceMismatchError} and tears the fresh connection
 * down — it is never returned, never swapped.
 */
export async function reconnectSameHardwareDevice(
  expectedFingerprint: string,
  connect: () => Promise<{ agent: KeyAgentClient; masterFingerprint: string }>,
  deriveFp: (raw: string) => Promise<string>,
): Promise<KeyAgentClient> {
  const { agent, masterFingerprint } = await connect();
  const fp = await deriveFp(masterFingerprint);
  if (fp !== expectedFingerprint) {
    try {
      agent.terminate();
    } catch {
      /* best-effort — releasing the wrong device must not mask the mismatch */
    }
    throw new HardwareDeviceMismatchError();
  }
  return agent;
}

/**
 * New-account seed setup found a seed already on the device. That seed is
 * the orphan of an earlier attempt that never completed registration —
 * seed setup is only reachable after the server reported no_account for
 * the email — so it is cleared and a fresh one minted. Because clearSeed
 * is the one destructive call in the agent (wiping a seed a live account
 * depends on would orphan the user's identity and vault keys), the
 * no_account status is re-confirmed with the server immediately before
 * clearing rather than trusting the caller's earlier lookup. Pure and
 * dependency-injected, mirroring {@link reconnectSameHardwareDevice}.
 */
export async function clearOrphanSeedForNewAccount(
  agent: Pick<KeyAgentClient, 'hasSeed' | 'clearSeed'>,
  recheckStatus: () => Promise<PasscodeCheckStatus>,
): Promise<void> {
  if (!(await agent.hasSeed())) return;
  const status = await recheckStatus();
  if (status !== 'no_account') {
    throw new Error(
      'An account already exists for this email — go back and sign in instead',
    );
  }
  await agent.clearSeed();
}

/**
 * COB-476: map an `/offer` outcome to the per-account offer-gate signal
 * recorded on the session (`offerGated` / `noteOfferGated`). Only definitive
 * outcomes speak: a live offer or `no_viable_spread` can only come from an
 * eligible account (trading 422s after passing its own gate), and
 * `not_available_to_account` is a recorded gate-out decision. `maintenance`,
 * `gate_unavailable`, unknown reason codes, and thrown failures are
 * indeterminate — `null`, leave the last observation alone (fail-safe: the
 * KYC banner keeps showing, and COB-486's Strategies tab stays live).
 *
 * COB-486 §5: this lives here rather than in pages/home.tsx (its original
 * home) because the session now resolves the gate itself — see the probe in
 * `SessionProvider` — and session-context must not import a page module.
 * `pages/home.tsx` re-exports it, so its own callers and tests are unchanged.
 */
export function offerGateSignal(res: OfferResult): boolean | null {
  if (!isOfferUnavailable(res)) return false;
  switch (res.reason) {
    case 'not_available_to_account':
      return true;
    // COB-549 (GF-4): a region gate-out is just as definitive a decision as
    // `not_available_to_account` — the gate answered, and the answer was no.
    // It therefore suppresses the KYC banner and greys the Strategies tab
    // for the same reason: a gated account cannot accept an offer or create
    // a vault whatever its KYC state, so nudging it toward a paid Sumsub
    // check is noise. It changes nothing about servicing — every existing
    // vault stays viewable, signable, withdrawable.
    case 'region_restricted':
      return true;
    case 'no_viable_spread':
      return false;
    default:
      return null;
  }
}

/** COB-486 §5: whether the session should fire its offer-gate probe (see the
 *  effect in `SessionProvider`). Three conditions, all necessary: the caller
 *  is signed in (an anonymous `/offer` says nothing about an account), the
 *  gate is still unresolved (`null` — a recorded observation, including one
 *  Home just made, is never re-fetched), and this identity has not already
 *  observed the gate *definitively* (the once-guard, reset on identity
 *  change). COB-505: `alreadyProbed` means "definitively probed", not
 *  "attempted" — a thrown or indeterminate `/offer` leaves it false so a
 *  later effect run retries. Pure + exported so the truth table is
 *  unit-tested without mounting a provider. */
export function shouldProbeOfferGate(
  status: SessionStatus,
  offerGated: boolean | null,
  alreadyProbed: boolean,
): boolean {
  return status === 'authenticated' && offerGated === null && !alreadyProbed;
}

/** COB-505: the body of the session's offer-gate probe, lifted out of the
 *  effect so all four arms are unit-testable (no harness in this suite mounts
 *  a `SessionProvider`). Exactly one rule: **only a definitive observation
 *  counts**. A `true`/`false` from `offerGateSignal` records the gate and
 *  burns the once-guard; an indeterminate `null` (maintenance,
 *  gate_unavailable, unknown reason) or a thrown call does NEITHER, leaving
 *  the guard unset so a later effect run — a status transition such as
 *  lock→unlock, or an identity change — retries naturally.
 *
 *  Before COB-505 the guard was burned before the await, so one transient
 *  blip stranded `offerGated === null` for the rest of the session: a
 *  gated-out account kept the KYC banner (COB-476 suppression never engaged)
 *  and saw the Strategies tab render live.
 *
 *  Fail-open stays sacred: an unreachable or indeterminate `/offer` never
 *  greys out a tab, so the error is swallowed rather than surfaced. */
export async function runOfferGateProbe(
  getOffer: () => Promise<OfferResult>,
  record: (gated: boolean) => void,
  markProbed: () => void,
): Promise<void> {
  try {
    const gate = offerGateSignal(await getOffer());
    if (gate === null) return; // indeterminate — retry on a later effect run
    markProbed();
    record(gate);
  } catch {
    /* indeterminate — leave the observation unknown and the guard unset */
  }
}

export type SessionStatus =
  | 'unauthenticated'
  | 'authenticated'
  | 'locked'
  | 'expired';

/** Which key-agent backend the current session runs on. UI gates that
 *  are backend-specific (seed reveal, on-device confirm hints) key off
 *  this rather than sniffing agent behaviour. */
export type AgentKind = 'software' | 'web3auth' | 'hardware';

export interface Session {
  status: SessionStatus;
  /** True iff `status === 'authenticated'`. Kept as a convenience
   *  for UI gates that previously checked `isAuthenticated`. */
  isAuthenticated: boolean;
  /** True iff the agent exists and the passphrase is still in
   *  memory (authenticated OR locked). Kept for UI gates that
   *  previously checked `isLoggedIn`; most call sites should
   *  prefer `isAuthenticated` now. */
  isLoggedIn: boolean;
  /** F17.1 — seconds until idle-timeout fires, surfaced only inside
   *  the countdown threshold (i.e. ≤ 60s by default). `null` when
   *  logged out, or when there's plenty of time left. UI renders a
   *  countdown chip when this is non-null. */
  idleSecondsRemaining: number | null;
  agent: KeyAgentClient | null;
  /** HW.3: which backend `agent` is. `null` when logged out. */
  agentKind: AgentKind | null;
  fingerprint: string | null;
  identityPubkey: string | null;
  /** Email the session was established with. Lower-cased. `null`
   *  before login and after logout. Surfaces for UI that needs to
   *  re-call `passcodeCheck` without re-asking the user for it
   *  (e.g. the wallet seed-reveal challenge). */
  userEmail: string | null;
  /** Server-side KYC status. `null` until first auth completes. */
  kycStatus: string | null;
  /** COB-340: whether the account's email is server-verified. `null`
   *  until first auth completes; kept current by every auth commit and
   *  by `refreshEmailVerification`. Drives the verify-email banner and
   *  gates verified-email surfaces (in-app shared-vault accept). */
  emailVerified: boolean | null;
  /** COB-476: the per-account offer gate (COB-400), as last observed by an
   *  `/offer` fetch. `true` = gated out (`not_available_to_account`),
   *  `false` = definitively eligible, `null` = no definitive observation
   *  yet this session. Suppresses the Layout's KYC banner while `true` —
   *  a gated account cannot accept offers or create vaults regardless of
   *  KYC status, so the verification nudge (a paid Sumsub check) is noise.
   *  Resets whenever the signed-in identity changes. */
  offerGated: boolean | null;
  /** COB-548 (GF-3): the geofence tier the backend put this viewer's
   *  country in, observed from a `451` on any request. `null` = nothing
   *  has been refused this session (the normal case, and the only one on
   *  a rig where the geofence is off).
   *
   *  `'prohibited'` short-circuits the whole SessionProvider subtree to
   *  the geo screen; `'servicing'` is a banner plus a suppressed
   *  acquisition surface — the user's existing vaults stay fully usable,
   *  which is the entire reason the tier exists. Never reset: a country
   *  does not change mid-session, and re-arming would let the SPA
   *  oscillate between gated and not. */
  geoTier: GeoTier | null;
  /** COB-549 (GF-4): whether this account has recorded a self-attested
   *  country of residence, from `GET /auth/residency/status`. `null` until
   *  the probe answers, and it stays `null` if the probe fails — a failed
   *  read must never claim the user has attested.
   *
   *  Purely a render input: it picks which flavour of the `region_restricted`
   *  offer card is shown — "confirm where you live" (with the CTA that opens
   *  the modal) when `false`/`null`, the final refusal when `true`. Since
   *  COB-582 it no longer opens any modal by itself. It gates NOTHING — the
   *  region decision lives at productco-kyc, and servicing never consults
   *  this. */
  residencyAttested: boolean | null;
  /** COB-549: the attested ISO-3166-1 alpha-2 code, or `null`. Display-only
   *  (the "not available in your region" card does not name it, but the
   *  modal shows the current selection when re-opened). */
  residencyJurisdiction: string | null;
  /** Lower-case bitcoin network from `GET /version`. `null` until that
   *  bootstrap call resolves, AND (COB-559) if it fails — a version-fetch
   *  failure means the network is unknown, not "assume mainnet." Every
   *  consumer must treat `null` as "suppress the mainnet-only / per-network
   *  affordance," never fall back to a guessed network. */
  bitcoinNetwork: string | null;
  /** F12: base64-decoded public salt for Argon2id verifier derivation.
   *  Fetched once from /version on mount. `null` until that completes. */
  passcodePublicSalt: Uint8Array | null;

  // ── F12 onboarding methods ────────────────────────────────────────

  /** Derive verifier + call /auth/passcode-check. Returns the status. */
  passcodeCheck: (email: string, passcode: string) => Promise<PasscodeCheckStatus>;
  /** Full "new account" flow: unlock agent with passcode, initialize
   *  seed, POST /auth/register with SIWB signature. Caller is
   *  responsible for walking the seed backup UI before invoking.
   *  Returns the generated mnemonic so the UI can display it for
   *  backup during the flow. */
  beginNewAccount: (email: string, passcode: string) => Promise<string>;
  /** Complete the new-account flow after the user has confirmed their
   *  seed backup. Issues the POST /auth/register. */
  completeNewAccount: (email: string, passcode: string) => Promise<void>;
  /** Unlock an existing local seed and run SIWB verify. Used for the
   *  `match` + local seed branch of the auth flow. */
  unlockAndVerify: (email: string, passcode: string) => Promise<void>;
  /** Restore a seed phrase on a device that has no local seed, then
   *  run SIWB verify. If the recovered pubkey does not match the
   *  server row for the given email, throws `ApiError(401)`. Used for
   *  the `match` + no local seed branch. */
  restoreAndVerify: (email: string, mnemonic: string, passcode: string) => Promise<void>;
  /** Forgot-passcode recovery: restore seed + set a new passcode +
   *  POST /auth/reset-passcode. Used for the `mismatch` → recover
   *  branch. */
  resetPasscodeWithSeed: (
    email: string,
    mnemonic: string,
    newPasscode: string,
  ) => Promise<void>;

  /** Web3Auth (MetaMask Embedded Wallets) login: opens the PnP modal,
   *  reconstructs the user's secp256k1 key client-side, injects it into
   *  a web3auth-mode key-agent Worker, then runs SIWB verify. No passcode
   *  or seed phrase.
   *
   *  COB-345: verify-first. The identity pubkey resolves the account, so a
   *  returning user is signed in with no email prompt even when the SDK
   *  withholds the email (it is hydrated from the status endpoint). A
   *  brand-new account (`unknown_user`) with a provider email throws
   *  {@link Web3AuthLossAckRequiredError} and stashes the live agent +
   *  register parameters so {@link completeWeb3AuthRegistration} can finish
   *  once the user acknowledges the login-loss consequence (COB-272); with
   *  NO provider email it throws {@link Web3AuthEmailRequiredError} and
   *  stashes the live agent so {@link completeWeb3AuthEmailRegistration}
   *  can finish with a typed email. */
  loginWithWeb3Auth: () => Promise<void>;

  /** COB-272: finish a new-account Web3Auth sign-in after the user has
   *  acknowledged that losing the social login means losing the signing
   *  key. Reads the agent + register parameters stashed by
   *  `loginWithWeb3Auth`'s register branch (throws if none are pending) and
   *  fires the deferred register. On the one-account-per-email 409 throws
   *  {@link EmailHasLocalAccountError} (the acknowledgement panel bounces
   *  to Self Custody with the email prefilled). */
  completeWeb3AuthRegistration: () => Promise<void>;

  /** COB-345: finish a provider-less Web3Auth sign-in by registering the
   *  account under a typed email. Reads the agent stashed by
   *  `loginWithWeb3Auth`'s capture branch (throws if none is pending), then
   *  registers via the typed branch (no `email_source`/idToken ⇒ the server
   *  fires its own verification round-trip). On the one-account-per-email
   *  409 throws {@link Web3AuthEmailTakenError} (the capture panel shows it
   *  inline — no bounce to the passcode panel). */
  completeWeb3AuthEmailRegistration: (typedEmail: string) => Promise<void>;

  /** COB-345/COB-272: abandon a pending Web3Auth sign-in (email capture or
   *  loss acknowledgement) — terminate the stashed agent worker and clear
   *  the half-committed session state (agent/kind/fingerprint/userEmail),
   *  mirroring `loginWithHardware`'s catch-teardown so the chooser starts
   *  clean. */
  cancelWeb3AuthPending: () => void;

  /** HW.3: hardware (Ledger) login. Connects over WebHID — the caller
   *  must invoke this from a user gesture (the panel's connect click) —
   *  then runs SIWB verify with the device's interim browser-held
   *  identity key, registering the account passwordlessly on first use.
   *  `email` is the account anchor for a first-time registration; for a
   *  returning device it is display-state only (the account resolves by
   *  identity pubkey) — device-native login (HW.6) retires that quirk. */
  loginWithHardware: (email: string) => Promise<void>;

  /** COB-336: reconnect the hardware device on a retry, without a new web
   *  session. Re-runs the WebHID connect (caller MUST invoke inside the
   *  retry click's user gesture), asserts the reconnected device matches the
   *  session's (mismatch → `HardwareDeviceMismatchError`, no swap), swaps in
   *  the fresh agent, and returns it. Only valid on a hardware session — the
   *  recovery path for a locked/disconnected/wedged Ledger (`DeviceUnresponsive`
   *  / `DeviceDisconnected`). */
  reconnectHardwareAgent: () => Promise<KeyAgentClient>;

  // ── KYC + logout ──────────────────────────────────────────────────

  /** Phase B: reconcile KYC status against productco-kyc (poll-on-demand)
   *  and update `kycStatus`. Replaces the old auto-pass `completeKyc`; the
   *  Sumsub WebSDK modal that drives a real verification is Phase C. */
  refreshKycStatus: () => Promise<void>;
  /** COB-476: record a definitive offer-gate observation (see
   *  `offerGated`). Callers pass only decided outcomes — indeterminate
   *  ones (`maintenance`, `gate_unavailable`, fetch failures) must leave
   *  the last observation alone rather than call this with a guess. */
  noteOfferGated: (gated: boolean) => void;
  /** COB-549 (GF-4): record a successful residency attestation, from the
   *  jurisdiction productco-kyc echoed back (never the one the client sent).
   *  Flips `residencyAttested` to true, which switches the region-restricted
   *  offer card from "confirm where you live" to the final refusal. */
  noteResidencyAttested: (jurisdiction: string) => void;
  /** COB-340: re-issue the verification email for the caller's own
   *  account email. Returns the server's status so the banner can say
   *  "sent" vs "already verified". */
  resendVerificationEmail: () => Promise<'ok' | 'already_verified'>;
  /** COB-340: re-read verified status from the server (poll-on-demand —
   *  the round-trip completes in another tab, so the banner offers an
   *  explicit refresh instead of polling). */
  refreshEmailVerification: () => Promise<void>;
  /** COB-346: point an UNVERIFIED account's email at a new address (typo
   *  correction) and re-fire the verification link. Passwordless sessions
   *  only — the server 409-refuses passcode accounts (email-derived
   *  Argon2id verifier), so callers gate the affordance on
   *  {@link changeEmailAvailable}. On success updates `userEmail`
   *  (`emailVerified` stays false, so the banner remains but now names
   *  the new address and resend targets it server-side). A 409 is
   *  disambiguated WITHOUT string-matching (see
   *  {@link resolveChangeEmailConflict}): either self-heals a stale
   *  banner (the account verified elsewhere) or throws
   *  {@link ChangeEmailTakenError}. */
  changeEmail: (newEmail: string) => Promise<void>;
  logout: () => void;
}

function defaultMethodReject<T>(): Promise<T> {
  return Promise.reject(new Error('Session not mounted'));
}

const defaultSession: Session = {
  status: 'unauthenticated',
  isAuthenticated: false,
  isLoggedIn: false,
  idleSecondsRemaining: null,
  agent: null,
  agentKind: null,
  fingerprint: null,
  identityPubkey: null,
  userEmail: null,
  kycStatus: null,
  emailVerified: null,
  offerGated: null,
  geoTier: null,
  residencyAttested: null,
  residencyJurisdiction: null,
  bitcoinNetwork: null,
  passcodePublicSalt: null,
  passcodeCheck: defaultMethodReject,
  beginNewAccount: defaultMethodReject,
  completeNewAccount: defaultMethodReject,
  unlockAndVerify: defaultMethodReject,
  restoreAndVerify: defaultMethodReject,
  resetPasscodeWithSeed: defaultMethodReject,
  loginWithWeb3Auth: defaultMethodReject,
  completeWeb3AuthRegistration: defaultMethodReject,
  completeWeb3AuthEmailRegistration: defaultMethodReject,
  cancelWeb3AuthPending: () => {},
  loginWithHardware: defaultMethodReject,
  reconnectHardwareAgent: defaultMethodReject,
  refreshKycStatus: defaultMethodReject,
  noteOfferGated: () => {},
  noteResidencyAttested: () => {},
  resendVerificationEmail: defaultMethodReject,
  refreshEmailVerification: defaultMethodReject,
  changeEmail: defaultMethodReject,
  logout: () => {},
};

const SessionContext = createContext<Session>(defaultSession);

export function useSession(): Session {
  return useContext(SessionContext);
}

/** Base64 → Uint8Array. */
function base64Decode(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Unwrap the bare DER hex from a proof for the frozen legacy passcode
 *  endpoints (`/auth/register`, `/auth/reset-passcode` — WS-H spec §8
 *  keeps them bare-signature). Software backends always emit
 *  `der-sha256-v1`, and a hardware session can never reach a passcode
 *  flow, so any other format here is a programming error. */
function derSignatureHex(proof: AppSignatureProof): string {
  if (proof.format !== 'der-sha256-v1') {
    throw new Error(`unexpected proof format for a passcode flow: ${proof.format}`);
  }
  return proof.signature_der_hex;
}

export function SessionProvider({ children }: { children: ComponentChildren }) {
  const [status, setStatus] = useState<SessionStatus>('unauthenticated');
  const [agent, setAgent] = useState<KeyAgentClient | null>(null);
  const [agentKind, setAgentKind] = useState<AgentKind | null>(null);
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const [identityPubkey, setIdentityPubkey] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);
  // F12.D: refresh-token TTL (24h wall-clock) is the terminal floor
  // for the session. Auto-refreshes on activity (each successful
  // /auth/refresh response carries a fresh 24h `refresh_expires_at_unix`
  // that we track here). The expiry timer anchors on this, not on
  // `tokenExpiresAt`, so access-token rollover is invisible to the
  // user — only genuine inactivity past the refresh TTL kicks them to
  // the 'expired' state.
  const [refreshTokenExpiresAt, setRefreshTokenExpiresAt] = useState<number | null>(null);
  const [kycStatus, setKycStatus] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [offerGated, setOfferGated] = useState<boolean | null>(null);
  // COB-548 (GF-3): sticky once set — see the `geoTier` doc on `Session`.
  const [geoTier, setGeoTier] = useState<GeoTier | null>(null);
  // COB-549 (GF-4): residency attestation state. See the docs on `Session`.
  const [residencyAttested, setResidencyAttested] = useState<boolean | null>(null);
  const [residencyJurisdiction, setResidencyJurisdiction] = useState<string | null>(
    null,
  );
  const [bitcoinNetwork, setBitcoinNetwork] = useState<string | null>(null);
  const [passcodePublicSalt, setPasscodePublicSalt] = useState<Uint8Array | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const agentRef = useRef<KeyAgentClient | null>(null);
  agentRef.current = agent;
  // COB-345: the live agent from a pending Web3Auth sign-in, held between
  // `loginWithWeb3Auth` throwing (`Web3AuthEmailRequiredError` or
  // `Web3AuthLossAckRequiredError`) and the matching complete call finishing
  // the register. A plain ref (never React state) for the same reason
  // `doPasswordlessRegister` takes the agent by argument: a transient
  // `unknown_user` 401 may already have flipped React session state, so the
  // register must use this local handle.
  const pendingWeb3AuthAgentRef = useRef<KeyAgentClient | null>(null);
  // COB-272: the register parameters deferred by the provider-email branch
  // while the user acknowledges the login-loss consequence. Set together
  // with `pendingWeb3AuthAgentRef`; consumed by `completeWeb3AuthRegistration`.
  const pendingWeb3AuthRegistrationRef = useRef<{
    email: string;
    emailSource?: 'provider' | 'web3auth_otp';
    idToken?: string;
  } | null>(null);
  const statusRef = useRef<SessionStatus>(status);
  statusRef.current = status;
  // COB-336: reconnectHardwareAgent runs inside a click handler and must see
  // the live backend + device fingerprint, not a stale render closure.
  const agentKindRef = useRef<AgentKind | null>(null);
  agentKindRef.current = agentKind;
  const fingerprintRef = useRef<string | null>(null);
  fingerprintRef.current = fingerprint;

  // Bootstrap: fetch /version on mount so we know the network (needed
  // by key-agent construction) and the public passcode salt (needed
  // for verifier derivation). Held in a ref so the async auth methods
  // can await it. F17.1 — also drives the bootstrap-unreachable
  // surface: if the call fails we render a managed retry screen
  // instead of letting the user click around with a half-initialised
  // SPA.
  const versionPromiseRef = useRef<Promise<{
    network: string;
    salt: Uint8Array;
  }> | null>(null);
  const [bootstrapStatus, setBootstrapStatus] = useState<
    'loading' | 'ready' | 'unreachable'
  >('loading');
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const realPromise = api.version().then((v) => ({
      network: v.bitcoin_network,
      salt: base64Decode(v.passcode_public_salt_b64),
    }));
    // Auth methods await this ref. Catch to a fail-closed sentinel
    // (mainnet + zero salt) so a downstream caller can't crash the
    // SPA on a version fetch failure — the zero salt is intentionally
    // wrong so the server rejects derivations rather than silently
    // accepting one against the wrong network. The unreachable screen
    // wired below normally takes over before any auth attempt happens;
    // this fallback is defence-in-depth for in-flight calls.
    versionPromiseRef.current = realPromise.catch(() => ({
      network: 'bitcoin',
      salt: new Uint8Array(32),
    }));
    realPromise
      .then(({ network, salt }) => {
        if (cancelled) return;
        setBitcoinNetwork(network);
        setPasscodePublicSalt(salt);
        setBootstrapStatus('ready');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // COB-559: fail closed — an unreachable /version means we do not
        // know the network, so `bitcoinNetwork` must stay the "unknown"
        // sentinel rather than default to a concrete (mainnet) one. See
        // `UNKNOWN_BITCOIN_NETWORK`'s doc for why this must never become
        // `'bitcoin'` again.
        setBitcoinNetwork(UNKNOWN_BITCOIN_NETWORK);
        setPasscodePublicSalt(new Uint8Array(32));
        // COB-548 (GF-3): `GET /version` is the SPA's very first call, so
        // a prohibited viewer meets the geofence here. That is a refusal,
        // not a network blip — "Can't reach Cobault / Try again" would be
        // both wrong and an invitation to retry forever, so the geo gate
        // (raised by the setOnGeoRestricted hook below) takes the screen
        // and bootstrap is left out of the `unreachable` state.
        if (e instanceof GeoRestrictedError) return;
        setBootstrapStatus('unreachable');
      });
    return () => {
      cancelled = true;
    };
  }, [bootstrapAttempt]);

  const handleBootstrapRetry = useCallback(() => {
    setBootstrapStatus('loading');
    setBootstrapAttempt((n) => n + 1);
  }, []);

  // F8: subscribe to key-agent lock/unlock CustomEvents so
  // sessionStatus stays in sync without polling.
  useEffect(() => {
    function onLocked() {
      // Only transition if we were previously authenticated — an
      // explicit logout already set status to 'unauthenticated' and
      // we don't want to overwrite that with 'locked'.
      if (statusRef.current === 'authenticated') {
        setStatus('locked');
      }
    }
    function onUnlocked() {
      // Noop — authenticated status is set by the verify/register
      // handlers, not by the unlock event. The event only exists so
      // we can react to the auto-inactivity-lock path.
    }
    window.addEventListener('cobault:agent-locked', onLocked);
    window.addEventListener('cobault:agent-unlocked', onUnlocked);
    return () => {
      window.removeEventListener('cobault:agent-locked', onLocked);
      window.removeEventListener('cobault:agent-unlocked', onUnlocked);
    };
  }, []);

  // F8: 401 → sessionStatus = 'expired'. api.ts fires this callback
  // whenever any request gets 401. It's a module-level hook and must
  // be registered on mount, unregistered on unmount.
  useEffect(() => {
    setOnUnauthorized(() => {
      // Don't flap if we're already unauthenticated.
      if (statusRef.current !== 'unauthenticated') {
        setStatus('expired');
        setIdentityPubkey(null);
        setTokenExpiresAt(null);
        setRefreshTokenExpiresAt(null);
      }
    });
    return () => setOnUnauthorized(null);
  }, []);

  // COB-548 (GF-3): 451 on any request → raise the geo gate. Module-level
  // hook, same shape as setOnUnauthorized: registered on mount,
  // unregistered on unmount.
  //
  // One-way by design. A `prohibited` observation never downgrades to
  // `servicing` (the strict tier wins), and neither is ever cleared —
  // clearing would let a servicing user who happened to get one 200 back
  // see the acquisition surface they cannot use.
  useEffect(() => {
    setOnGeoRestricted((tier) => {
      setGeoTier((current) => (current === 'prohibited' ? current : tier));
    });
    return () => setOnGeoRestricted(null);
  }, []);

  // F12.D: every transparent refresh fires this callback so React
  // state tracks the new access + refresh token TTLs. This is what
  // keeps the terminal-expiry timer sliding with activity rather
  // than locked to the original refresh-token mint time.
  useEffect(() => {
    setOnRefresh((res) => {
      setTokenExpiresAt(res.expires_at_unix);
      setRefreshTokenExpiresAt(res.refresh_expires_at_unix);
    });
    return () => setOnRefresh(null);
  }, []);

  // F17.1 — idle-timeout state + cross-tab session sync.
  //
  // Two new behaviours bolted on to the existing session model:
  //  - 5-minute inactivity window (COB-385; mouse / keyboard / scroll /
  //    touch). Once the idle clock hits zero we transition
  //    status='expired' and surface a session-expired modal. This is
  //    the user-visible "timeout" — the access token outlives it
  //    (JWT_TTL_SECS 900) but the client discards its tokens here, so
  //    the idle clock is the effective session boundary.
  //  - BroadcastChannel('cobault-session') so every open tab sees
  //    activity in any other tab (resets the idle clock everywhere)
  //    and so a logout / expiry in one tab logs out every tab.
  const [idleSecondsRemaining, setIdleSecondsRemaining] = useState<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const lastBroadcastRef = useRef<number>(0);
  const lastIntentfulBroadcastRef = useRef<number>(0);
  const lastWalletTouchRef = useRef<number>(0);
  const idleTickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionChannelRef = useRef<BroadcastChannel | null>(null);

  /** Reset the idle clock to "now". `broadcast=true` also tells other
   *  tabs we're active so theirs resets too — throttled to 1Hz so a
   *  burst of mousemoves doesn't flood the channel.
   *
   *  COB-385: `intentful` marks deliberate input (keydown / click /
   *  touchstart — not mousemove / scroll). Only intentful activity
   *  refreshes the key agent's wallet-lock clock, so key material stays
   *  live while the user is actually doing things, not merely because a
   *  cursor drifted. Intentful pings get their own 1Hz broadcast
   *  throttle so a mousemove burst can't starve them off the channel. */
  const touchActivity = useCallback((broadcast: boolean, intentful: boolean) => {
    const now = Date.now();
    lastActivityRef.current = now;
    if (intentful && now - lastWalletTouchRef.current >= 1000) {
      lastWalletTouchRef.current = now;
      // touch() is a no-op on a locked agent; an already-elapsed window
      // locks lazily inside the Worker here, firing cobault:agent-locked
      // so the badge flips on the first interaction after expiry instead
      // of on the next failed sign attempt.
      void agentRef.current?.touch();
    }
    if (broadcast) {
      const throttle = intentful ? lastIntentfulBroadcastRef : lastBroadcastRef;
      if (now - throttle.current >= 1000) {
        throttle.current = now;
        // An intentful ping doubles as plain activity for peers, so it
        // also consumes the plain-activity throttle slot.
        lastBroadcastRef.current = now;
        sessionChannelRef.current?.postMessage({ type: 'activity', t: now, intentful });
      }
    }
  }, []);

  // User-input listeners: any of these resets the idle clock. Captured
  // at window level with passive+capture so we never block the page.
  useEffect(() => {
    const handler = (ev: Event) =>
      touchActivity(true, INTENTFUL_ACTIVITY_EVENTS.has(ev.type));
    const events: Array<keyof WindowEventMap> = [
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];
    for (const ev of events) {
      window.addEventListener(ev, handler, { passive: true, capture: true });
    }
    return () => {
      for (const ev of events) {
        window.removeEventListener(ev, handler, { capture: true });
      }
    };
  }, [touchActivity]);

  // BroadcastChannel for cross-tab activity + logout. Optional: older
  // browsers without BroadcastChannel just degrade to per-tab idle
  // timers, which is exactly the pre-F17.1 behaviour.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('cobault-session');
    sessionChannelRef.current = ch;
    ch.onmessage = (msg: MessageEvent) => {
      const data = msg.data as { type?: string; t?: number; intentful?: boolean };
      if (data?.type === 'activity' && typeof data.t === 'number') {
        // Adopt the peer's timestamp if it's newer than ours.
        if (data.t > lastActivityRef.current) {
          lastActivityRef.current = data.t;
        }
        // COB-385: a peer tab's intentful activity keeps this tab's
        // wallet unlocked too (each tab runs its own Worker/clock).
        // Same 1Hz throttle as the local touch path.
        if (data.intentful === true) {
          const now = Date.now();
          if (now - lastWalletTouchRef.current >= 1000) {
            lastWalletTouchRef.current = now;
            void agentRef.current?.touch();
          }
        }
      } else if (data?.type === 'logout') {
        if (
          statusRef.current === 'authenticated' ||
          statusRef.current === 'locked'
        ) {
          setStatus('expired');
          setIdentityPubkey(null);
          setTokenExpiresAt(null);
          setRefreshTokenExpiresAt(null);
        }
      }
    };
    return () => {
      ch.close();
      sessionChannelRef.current = null;
    };
  }, []);

  // Idle ticker — runs only while we're logged in (authenticated OR
  // locked). Updates the countdown chip and triggers expiry at zero.
  const isLoggedInForTimer =
    status === 'authenticated' || status === 'locked';
  useEffect(() => {
    if (idleTickRef.current) clearInterval(idleTickRef.current);
    idleTickRef.current = null;

    if (!isLoggedInForTimer) {
      setIdleSecondsRemaining(null);
      return;
    }

    // Re-arm the clock on every transition into a logged-in state so
    // a fresh login doesn't inherit a stale lastActivity from before.
    lastActivityRef.current = Date.now();

    idleTickRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remainingMs = IDLE_TIMEOUT_MS - elapsed;
      if (remainingMs <= 0) {
        // Idle expiry — broadcast first so peers transition in lockstep.
        sessionChannelRef.current?.postMessage({ type: 'logout' });
        setStatus('expired');
        setIdentityPubkey(null);
        setTokenExpiresAt(null);
        setRefreshTokenExpiresAt(null);
        setIdleSecondsRemaining(null);
        return;
      }
      if (remainingMs <= COUNTDOWN_THRESHOLD_MS) {
        setIdleSecondsRemaining(Math.ceil(remainingMs / 1000));
      } else {
        // Active — make sure the chip is hidden if we just bumped back
        // above the threshold (e.g. user moved the mouse during the
        // countdown).
        setIdleSecondsRemaining((prev) => (prev === null ? null : null));
      }
    }, IDLE_TICK_MS);

    return () => {
      if (idleTickRef.current) clearInterval(idleTickRef.current);
      idleTickRef.current = null;
    };
  }, [isLoggedInForTimer]);

  /** Run SIWB challenge-response and commit the resulting JWT + KYC
   *  status to React state. Shared between unlock, register, restore,
   *  and reset paths. HW.6 (COB-326): the agent returns a WS-H proof
   *  object (`der-sha256-v1` from software backends, `bsm-bip137-v1`
   *  from the device) and the funnel posts `/auth/verify-v2` for every
   *  backend — the legacy `/auth/verify` is frozen. For the hardware
   *  backend the device is already connected before the challenge is
   *  minted (gesture-before-nonce, spec §9.1), so the whole TTL is
   *  spent on the on-device approval. */
  const doSiwbVerify = useCallback(
    async (a: KeyAgentClient): Promise<void> => {
      const pubkey = await a.getIdentityPubkey();
      const { challenge } = await api.getChallenge(pubkey);
      const proof = await a.signChallenge(challenge);
      const {
        token,
        expires_at_unix,
        refresh_token,
        refresh_expires_at_unix,
        kyc_status,
        email_verified,
      } = await api.verifyChallengeV2({
        pubkey,
        challenge,
        proof,
      });
      setAuthToken(token);
      setRefreshToken(refresh_token);
      setAuthTokenExpiresAt(expires_at_unix);
      setIdentityPubkey(pubkey);
      setTokenExpiresAt(expires_at_unix);
      setRefreshTokenExpiresAt(refresh_expires_at_unix);
      setKycStatus(kyc_status);
      setEmailVerified(email_verified);
      setStatus('authenticated');
    },
    [],
  );

  /** Construct a Worker-backed key-agent client for the current
   *  network and unlock it. Returns the client so callers can chain
   *  seed setup / restore before committing it to React state. S06.5:
   *  the SoftwareKeyAgent itself lives in the Worker — the main
   *  thread only holds this proxy. */
  const buildAndUnlockAgent = useCallback(
    async (email: string, passcode: string): Promise<KeyAgentClient> => {
      const version = await versionPromiseRef.current!;
      const a = createKeyAgentClient(
        toKeyAgentNetwork(version.network),
        WALLET_LOCK_TIMEOUT_MS,
      );
      const normalized = email.trim().toLowerCase();
      await a.setEmail(normalized);
      await a.unlock(passcode);
      // Mirror the email into React state so re-auth UIs (seed reveal,
      // future re-challenge flows) can call passcodeCheck without
      // re-prompting for it. Normalized to match the Worker's own copy.
      setUserEmail(normalized);
      setAgentKind('software');
      return a;
    },
    [],
  );

  const passcodeCheck = useCallback(
    async (email: string, passcode: string): Promise<PasscodeCheckStatus> => {
      const { salt, network } = await versionPromiseRef.current!;
      // Short-lived Worker just for verifier derivation. The Worker is
      // terminated before we return so no background process survives
      // the check.
      const client = createKeyAgentClient(toKeyAgentNetwork(network));
      try {
        const verifier_hex = await client.deriveVerifier(email, passcode, salt);
        const res = await api.passcodeCheck({ email, verifier_hex });
        return res.status;
      } finally {
        client.terminate();
      }
    },
    [],
  );

  const beginNewAccount = useCallback(
    async (email: string, passcode: string): Promise<string> => {
      const a = await buildAndUnlockAgent(email, passcode);
      // Clear the orphan of any earlier attempt that never completed
      // registration, rather than dead-ending on "Master seed already
      // exists" (see clearOrphanSeedForNewAccount for the guard).
      await clearOrphanSeedForNewAccount(a, () => passcodeCheck(email, passcode));
      // Generate + encrypt the seed under the passcode. Returns the
      // mnemonic so the UI can display it for backup.
      const mnemonic = await a.initializeSeed();
      // Derive fingerprint + identity pubkey immediately so they are
      // available when register is called next.
      const { deriveFingerprint } = await import('./user-fingerprint.js');
      const fp = await deriveFingerprint(passcode);
      setAgent(a);
      setFingerprint(fp);
      return mnemonic;
    },
    [buildAndUnlockAgent, passcodeCheck],
  );

  const completeNewAccount = useCallback(
    async (email: string, passcode: string): Promise<void> => {
      const a = agentRef.current;
      if (!a) throw new Error('beginNewAccount must run first');
      // The agent auto-locks after its inactivity window, and writing
      // down 24 words routinely takes longer than that. Re-unlock with
      // the passcode in hand — the lock stays a security property of
      // the agent, but it must not strand the onboarding flow.
      await a.unlock(passcode);
      // Mark the seed as backup-confirmed in storage.
      await a.confirmBackup();

      const pubkey = await a.getIdentityPubkey();
      const { challenge } = await api.getChallenge(pubkey);
      const signature = derSignatureHex(await a.signChallenge(challenge));

      const { salt } = await versionPromiseRef.current!;
      const verifier_hex = await a.deriveVerifier(email, passcode, salt);

      const {
        token,
        expires_at_unix,
        refresh_token,
        refresh_expires_at_unix,
        kyc_status,
        email_verified,
      } = await api.registerEmail({
        email,
        verifier_hex,
        pubkey,
        challenge,
        signature,
      });
      setAuthToken(token);
      setRefreshToken(refresh_token);
      setAuthTokenExpiresAt(expires_at_unix);
      setIdentityPubkey(pubkey);
      setTokenExpiresAt(expires_at_unix);
      setRefreshTokenExpiresAt(refresh_expires_at_unix);
      setKycStatus(kyc_status);
      setEmailVerified(email_verified);
      setStatus('authenticated');
    },
    [],
  );

  const unlockAndVerify = useCallback(
    async (email: string, passcode: string): Promise<void> => {
      const a = await buildAndUnlockAgent(email, passcode);
      if (!(await a.hasSeed())) {
        // Caller guards against this path, but be explicit — never
        // silently mint a new identity here.
        throw new Error('No local seed; use restoreAndVerify instead');
      }
      const { deriveFingerprint } = await import('./user-fingerprint.js');
      const fp = await deriveFingerprint(passcode);
      setAgent(a);
      setFingerprint(fp);
      await doSiwbVerify(a);
    },
    [buildAndUnlockAgent, doSiwbVerify],
  );

  const restoreAndVerify = useCallback(
    async (email: string, mnemonic: string, passcode: string): Promise<void> => {
      const a = await buildAndUnlockAgent(email, passcode);
      // restoreSeed throws if a seed already exists on the device —
      // the caller is responsible for only invoking this when
      // hasSeed() is false.
      await a.restoreSeed(mnemonic);
      const { deriveFingerprint } = await import('./user-fingerprint.js');
      const fp = await deriveFingerprint(passcode);
      setAgent(a);
      setFingerprint(fp);
      // SIWB verify here may return 401 unknown_user if the recovered
      // seed's identity pubkey does not match the stored row for the
      // email — caller catches and routes to restoreWrongSeed.
      await doSiwbVerify(a);
    },
    [buildAndUnlockAgent, doSiwbVerify],
  );

  const resetPasscodeWithSeed = useCallback(
    async (email: string, mnemonic: string, newPasscode: string): Promise<void> => {
      const a = await buildAndUnlockAgent(email, newPasscode);
      // If the device already has a seed from a prior session, skip
      // restoreSeed (which would throw). The pubkey will still match
      // the server row only if the existing seed is the right one;
      // otherwise the server 401s and the caller surfaces that.
      if (!(await a.hasSeed())) {
        await a.restoreSeed(mnemonic);
      }
      const pubkey = await a.getIdentityPubkey();
      const { challenge } = await api.getChallenge(pubkey);
      const signature = derSignatureHex(await a.signChallenge(challenge));

      const { salt } = await versionPromiseRef.current!;
      const new_verifier_hex = await a.deriveVerifier(email, newPasscode, salt);

      const {
        token,
        expires_at_unix,
        refresh_token,
        refresh_expires_at_unix,
        kyc_status,
        email_verified,
      } = await api.resetPasscode({
        email,
        new_verifier_hex,
        pubkey,
        challenge,
        signature,
      });

      const { deriveFingerprint } = await import('./user-fingerprint.js');
      const fp = await deriveFingerprint(newPasscode);

      setAgent(a);
      setFingerprint(fp);
      setAuthToken(token);
      setRefreshToken(refresh_token);
      setAuthTokenExpiresAt(expires_at_unix);
      setIdentityPubkey(pubkey);
      setTokenExpiresAt(expires_at_unix);
      setRefreshTokenExpiresAt(refresh_expires_at_unix);
      setKycStatus(kyc_status);
      setEmailVerified(email_verified);
      setStatus('authenticated');
    },
    [buildAndUnlockAgent],
  );

  /** 0034: register a passwordless account and commit the resulting
   *  tokens. Mirrors `doSiwbVerify`'s commit but goes through
   *  `/auth/register-web3auth` (no verifier) — the server's one
   *  passwordless registration path, shared by Web3Auth and (since
   *  HW.6, device-native) hardware sessions. The SIWB gate travels as
   *  the WS-H `proof` field, so the hardware backend's BSM signature
   *  registers as-is. Takes the agent by argument so it uses the local
   *  handle even if a transient `unknown_user` 401 already flipped
   *  React session state to `expired`.
   *
   *  COB-340: `emailSource` + `idToken` carry the provider-verified
   *  email proof for a Web3Auth login. Both present ⇒ the server
   *  verifies the idToken and lands the row verified-at-insert; both
   *  absent ⇒ the typed / self-roundtrip path (hardware, or a Web3Auth
   *  session whose SDK didn't yield an idToken). */
  const doPasswordlessRegister = useCallback(
    async (
      a: KeyAgentClient,
      email: string,
      emailSource?: 'provider' | 'web3auth_otp',
      idToken?: string,
    ): Promise<void> => {
      const pubkey = await a.getIdentityPubkey();
      const { challenge } = await api.getChallenge(pubkey);
      const proof = await a.signChallenge(challenge);
      const {
        token,
        expires_at_unix,
        refresh_token,
        refresh_expires_at_unix,
        kyc_status,
        email_verified,
      } = await api.registerWeb3Auth({
        email,
        pubkey,
        challenge,
        proof,
        ...(emailSource && idToken
          ? { email_source: emailSource, web3auth_id_token: idToken }
          : {}),
      });
      setAuthToken(token);
      setRefreshToken(refresh_token);
      setAuthTokenExpiresAt(expires_at_unix);
      setIdentityPubkey(pubkey);
      setTokenExpiresAt(expires_at_unix);
      setRefreshTokenExpiresAt(refresh_expires_at_unix);
      setKycStatus(kyc_status);
      setEmailVerified(email_verified);
      setStatus('authenticated');
    },
    [],
  );

  const loginWithWeb3Auth = useCallback(async (): Promise<void> => {
    const version = await versionPromiseRef.current!;
    // Lazy-load the Web3Auth SDK so its (large) modal bundle only loads
    // when a user actually chooses this method.
    const { web3authConnectAndExtractKey } = await import('./web3auth-login.js');
    const { privateKeyHex, email, idToken, typeOfLogin } =
      await web3authConnectAndExtractKey();
    // COB-345: an email is NO LONGER required up front. The identity pubkey
    // is what resolves the account at verify — a returning user is known by
    // their key regardless of whether the provider shared an email. An email
    // is only needed to REGISTER a brand-new account, and a provider-less
    // sign-in supplies that via the capture step (see `capture-email` below),
    // so we no longer hard-stop here on a missing email.
    const normalizedEmail = email ? email.trim().toLowerCase() : null;
    // COB-340: classify the email's provenance for verified-at-insert.
    // `email_passwordless` is Web3Auth's own OTP round-trip (the user
    // proved mailbox control to Web3Auth); anything else with an idToken
    // is a social provider attestation. No idToken ⇒ register as typed
    // and fall back to our own verification round-trip.
    const emailSource: 'provider' | 'web3auth_otp' | undefined = idToken
      ? typeOfLogin === 'email_passwordless'
        ? 'web3auth_otp'
        : 'provider'
      : undefined;
    // web3auth-mode Worker: the agent reconstructs its keys from the
    // injected secp256k1 key (used as a BIP32 seed). The key crosses the
    // main thread once here (PnP); steady-state signing stays in the
    // Worker. (MPC Core Kit would remove even this transit — see plan.)
    const a = createKeyAgentClient(
      toKeyAgentNetwork(version.network),
      WALLET_LOCK_TIMEOUT_MS,
      'web3auth',
    );
    await a.loadWeb3AuthKey(privateKeyHex);
    // No passcode for web3auth — scope the local vault-store by a
    // fingerprint derived from the identity pubkey instead.
    const idPubkey = await a.getIdentityPubkey();
    const { deriveFingerprint } = await import('./user-fingerprint.js');
    const fp = await deriveFingerprint(idPubkey);
    // Commit the agent state (mirrors loginWithHardware's pre-verify commit).
    // `setUserEmail` ONLY when the provider actually gave us one — a
    // provider-less session's email is unknown until it either verifies (a
    // returning user, hydrated from the status endpoint) or registers (the
    // capture step); committing an empty string would show a blank email.
    if (normalizedEmail) setUserEmail(normalizedEmail);
    setAgent(a);
    setAgentKind('web3auth');
    setFingerprint(fp);
    // Verify-first. A known pubkey verifies first try (returning user, no
    // prompt); an unknown pubkey 401s `unknown_user` and we branch on whether
    // there's an email to register with. Capture the outcome so the pure
    // classifier decides — keeps the decision table testable.
    let outcome: Web3AuthVerifyOutcome;
    try {
      await doSiwbVerify(a);
      outcome = { ok: true };
    } catch (e) {
      outcome = { ok: false, error: e };
    }
    switch (decideWeb3AuthPostVerify(outcome, normalizedEmail !== null)) {
      case 'authenticated':
        // Returning user; the provider gave the email and it is already set.
        return;
      case 'hydrate-email': {
        // Returning user whose provider withheld the email. The auth token is
        // committed (verify succeeded), so read the account's own email off
        // the status endpoint and mirror it into session state.
        const statusResp = await api.getEmailVerificationStatus();
        setUserEmail(statusResp.email ?? null);
        return;
      }
      case 'register':
        // Brand-new account with a provider email in hand. COB-272: the
        // register itself is deferred — losing the social login means losing
        // the signing key, so the user must acknowledge that first. Stash
        // the LIVE agent + register parameters and signal the chooser to
        // route to the acknowledgement panel; `completeWeb3AuthRegistration`
        // fires the deferred register (verified-at-insert when an idToken
        // backs it, else the typed self-roundtrip) once acknowledged.
        pendingWeb3AuthAgentRef.current = a;
        pendingWeb3AuthRegistrationRef.current = {
          email: normalizedEmail!,
          emailSource,
          idToken: idToken ?? undefined,
        };
        throw new Web3AuthLossAckRequiredError();
      case 'capture-email':
        // Brand-new account, no provider email: stash the LIVE agent so the
        // capture panel can register it with a typed email, and signal the
        // chooser to route there. The agent is left committed to session
        // state; `cancelWeb3AuthPending` tears it down on Back.
        pendingWeb3AuthAgentRef.current = a;
        throw new Web3AuthEmailRequiredError();
      case 'rethrow':
        // Non-`unknown_user` verify failure — surface unchanged. `outcome`
        // is a failure here (the classifier only returns 'rethrow' on one),
        // so re-throw its error.
        throw (outcome as { ok: false; error: unknown }).error;
    }
  }, [doSiwbVerify, doPasswordlessRegister]);

  const completeWeb3AuthRegistration = useCallback(async (): Promise<void> => {
    const a = pendingWeb3AuthAgentRef.current;
    const reg = pendingWeb3AuthRegistrationRef.current;
    if (!a || !reg) {
      throw new Error(
        'No pending web wallet sign-in to finish. Start the web wallet ' +
          'sign-in again.',
      );
    }
    try {
      await doPasswordlessRegister(a, reg.email, reg.emailSource, reg.idToken);
    } catch (regErr) {
      // 409 → EmailHasLocalAccountError (the panel bounces to Self Custody
      // with the email prefilled); any other error passes through and shows
      // inline on the panel. Only the collision clears the stash — it
      // abandons this registration (parity with the pre-COB-272
      // immediate-register branch, which left the committed agent in place
      // on a bounce); a transient failure keeps it so the panel's button
      // can simply retry.
      const mapped = mapPrimaryRegisterError(regErr, reg.email);
      if (mapped instanceof EmailHasLocalAccountError) {
        pendingWeb3AuthAgentRef.current = null;
        pendingWeb3AuthRegistrationRef.current = null;
      }
      throw mapped;
    }
    pendingWeb3AuthAgentRef.current = null;
    pendingWeb3AuthRegistrationRef.current = null;
  }, [doPasswordlessRegister]);

  const completeWeb3AuthEmailRegistration = useCallback(
    async (typedEmail: string): Promise<void> => {
      const a = pendingWeb3AuthAgentRef.current;
      if (!a) {
        throw new Error(
          'No pending web wallet sign-in to attach an email to. Start the web ' +
            'wallet sign-in again.',
        );
      }
      const normalizedEmail = typedEmail.trim().toLowerCase();
      try {
        // Typed branch: no `emailSource`/idToken ⇒ the server treats the email
        // as unverified and fires its own verification round-trip — exactly the
        // COB-340 typed-registration rails.
        await doPasswordlessRegister(a, normalizedEmail);
      } catch (regErr) {
        // 409 → Web3AuthEmailTakenError (inline in the panel, no bounce); any
        // other error passes through. See `mapCaptureRegisterError`.
        throw mapCaptureRegisterError(regErr, normalizedEmail);
      }
      setUserEmail(normalizedEmail);
      pendingWeb3AuthAgentRef.current = null;
    },
    [doPasswordlessRegister],
  );

  const cancelWeb3AuthPending = useCallback((): void => {
    const a = pendingWeb3AuthAgentRef.current;
    if (a) {
      try {
        a.terminate();
      } catch {
        /* best-effort — a wedged worker must not block the teardown */
      }
    }
    pendingWeb3AuthAgentRef.current = null;
    pendingWeb3AuthRegistrationRef.current = null;
    // Clear the half-committed session state (mirrors loginWithHardware's
    // catch-teardown) so the chooser starts clean on Back.
    setAgent(null);
    setAgentKind(null);
    setFingerprint(null);
    setUserEmail(null);
  }, []);

  /** HW.3: hardware (Ledger) login — the chooser panel's connect click.
   *  Mirrors `loginWithWeb3Auth`'s shape: connect, commit the agent to
   *  state, SIWB verify, passwordless-register on `unknown_user`. The
   *  WebHID permission prompt fires inside `hardwareConnectAndBuildAgent`,
   *  so no network round-trip may precede it (transient user activation);
   *  the panel pre-warms the chunk import for the same reason. Unlike the
   *  Worker backends a failed login here tears the agent down again —
   *  leaving the HID interface claimed would make every retry fail with
   *  "device in use". */
  const loginWithHardware = useCallback(
    async (email: string): Promise<void> => {
      const version = await versionPromiseRef.current!;
      const normalizedEmail = email.trim().toLowerCase();
      const { hardwareConnectAndBuildAgent } = await import('./hardware-connect.js');
      const { agent: a, masterFingerprint } = await hardwareConnectAndBuildAgent(
        toKeyAgentNetwork(version.network),
      );
      try {
        // Scope the local vault-store by the DEVICE (master fingerprint)
        // — stable across browsers and consistent with the device-native
        // identity (HW.6): same device, same account, same local scope
        // everywhere.
        const { deriveFingerprint } = await import('./user-fingerprint.js');
        const fp = await deriveFingerprint(masterFingerprint);
        setUserEmail(normalizedEmail);
        setAgent(a);
        setAgentKind('hardware');
        setFingerprint(fp);
        try {
          await doSiwbVerify(a);
        } catch (e) {
          if (e instanceof ApiError && e.status === 401 && e.message === 'unknown_user') {
            try {
              await doPasswordlessRegister(a, normalizedEmail);
            } catch (regErr) {
              if (regErr instanceof ApiError && regErr.status === 409) {
                throw new EmailAlreadyRegisteredError(normalizedEmail);
              }
              throw regErr;
            }
          } else {
            throw e;
          }
        }
      } catch (e) {
        // Post-connect failure: release the device and clear the
        // half-committed session state so a retry starts clean.
        a.terminate();
        setAgent(null);
        setAgentKind(null);
        setFingerprint(null);
        setUserEmail(null);
        throw e;
      }
    },
    [doSiwbVerify, doPasswordlessRegister],
  );

  /** COB-336: reconnect the session's hardware device on a retry. See the
   *  interface doc. Refuses to run off a non-hardware session, and refuses
   *  to swap in a different device (fingerprint mismatch). The old (wedged)
   *  agent is released after the fresh one is committed. */
  const reconnectHardwareAgent = useCallback(async (): Promise<KeyAgentClient> => {
    if (agentKindRef.current !== 'hardware') {
      throw new Error('reconnectHardwareAgent is only valid on a hardware session.');
    }
    const expected = fingerprintRef.current;
    if (!expected) {
      throw new Error('No device fingerprint on the current session to match against.');
    }
    const version = await versionPromiseRef.current!;
    const { deriveFingerprint } = await import('./user-fingerprint.js');
    const { hardwareConnectAndBuildAgent } = await import('./hardware-connect.js');
    const fresh = await reconnectSameHardwareDevice(
      expected,
      () => hardwareConnectAndBuildAgent(toKeyAgentNetwork(version.network)),
      deriveFingerprint,
    );
    const previous = agentRef.current;
    setAgent(fresh);
    agentRef.current = fresh;
    // Release the old wedged transport now that the fresh agent is committed.
    if (previous && previous !== fresh) void previous.terminate();
    return fresh;
  }, []);

  const refreshKycStatus = useCallback(async () => {
    const { kyc_status } = await api.getKycStatus();
    setKycStatus(kyc_status);
  }, []);

  // COB-476: offer-gate observations are per-account, so any identity
  // change (login, account switch, logout clearing the pubkey) drops the
  // previous account's observation back to "unknown". This single reset
  // covers every auth-commit path and logout without touching each one.
  const noteOfferGated = useCallback((gated: boolean) => {
    setOfferGated(gated);
  }, []);
  // COB-486 §5: guards the resolve-on-mount probe below to ONE *definitive
  // observation* per signed-in identity (COB-505 — set only when `/offer`
  // actually answered gated or not-gated, never merely because a call was
  // attempted). A thrown or indeterminate outcome leaves it false so a later
  // effect run retries. Cleared alongside the observation on an identity
  // change so the next account resolves its own gate.
  const offerGateProbedRef = useRef(false);
  useEffect(() => {
    setOfferGated(null);
    offerGateProbedRef.current = false;
  }, [identityPubkey]);

  // COB-486 §5: resolve the offer gate once per signed-in identity even when
  // the user never lands on Home. Home's own `/offer` fetch used to be the
  // only thing that recorded the gate, so a gated user deep-linking to
  // /wallet or /shared-vaults kept `offerGated === null` and the nav rendered
  // the Strategies tab as if it were live. The probe is a plain re-use of the
  // same endpoint and the same `offerGateSignal` mapping, so racing Home's
  // fetch is harmless (both record the identical value; `/offer` is capped at
  // 60/min per IP, and this adds one call per identity — plus, since COB-505,
  // one per subsequent status transition until the gate answers definitively,
  // which is a handful at worst).
  //
  // Fails SILENTLY and leaves the observation `null`: an unreachable or
  // indeterminate `/offer` must never grey out a tab. Only a recorded
  // `not_available_to_account` does. COB-505: a silent failure also leaves
  // the once-guard unset, so the next run of this effect (a status
  // transition such as lock→unlock, or an identity change resetting state)
  // retries. That is deps-gated, not render-gated — refs don't re-render —
  // so there is no retry storm.
  useEffect(() => {
    if (!shouldProbeOfferGate(status, offerGated, offerGateProbedRef.current)) {
      return;
    }
    void runOfferGateProbe(api.getOffer, setOfferGated, () => {
      offerGateProbedRef.current = true;
    });
  }, [status, offerGated]);

  // ── COB-549 (GF-4): residency attestation ───────────────────────────

  const noteResidencyAttested = useCallback((jurisdiction: string) => {
    setResidencyAttested(true);
    setResidencyJurisdiction(jurisdiction);
  }, []);

  // Per-account, exactly like the offer-gate observation: an identity change
  // (login, account switch, logout) drops the previous account's answer, so
  // one user's attested jurisdiction is never rendered against another's
  // session.
  useEffect(() => {
    setResidencyAttested(null);
    setResidencyJurisdiction(null);
  }, [identityPubkey]);

  // Resolve the attestation state once the session is authenticated, so Home
  // knows which `region_restricted` card to render. One local read
  // (`GET /auth/residency/status` hits user-app's own columns — no
  // productco-kyc round trip), fired only while the answer is unknown.
  //
  // Fails SILENTLY, leaving the state `null`: `null` never claims the user
  // has attested. `offerUnavailableCard` treats it as "not attested", which
  // renders the card that offers a way to answer rather than one that
  // announces a refusal we cannot substantiate.
  useEffect(() => {
    if (status !== 'authenticated' || residencyAttested !== null) return;
    let cancelled = false;
    api
      .getResidencyStatus()
      .then((r) => {
        if (cancelled) return;
        setResidencyAttested(r.attested);
        setResidencyJurisdiction(r.jurisdiction);
      })
      .catch(() => {
        /* indeterminate — leave it unknown; a later run retries */
      });
    return () => {
      cancelled = true;
    };
  }, [status, residencyAttested]);

  // COB-340: banner actions. Resend re-issues the link for the caller's
  // own row; refresh re-reads verified status (the round-trip completes
  // in another tab, so the user clicks rather than the SPA polling).
  const resendVerificationEmail = useCallback(async () => {
    const { status } = await api.verifyEmailResend();
    if (status === 'already_verified') setEmailVerified(true);
    return status;
  }, []);

  const refreshEmailVerification = useCallback(async () => {
    const { email_verified } = await api.getEmailVerificationStatus();
    setEmailVerified(email_verified);
  }, []);

  // COB-346: correct the email on an UNVERIFIED account. On success the
  // banner stays (still unverified) but now names the new address and
  // resend targets it server-side. The one server 409 is ambiguous
  // between "target taken by another account" and "this account already
  // verified" and the SPA can't string-match the body — so on 409 we
  // re-read our own status and let the pure `resolveChangeEmailConflict`
  // decide (self-heal a stale banner vs. surface the taken-by-other copy).
  const changeEmail = useCallback(
    async (newEmail: string): Promise<void> => {
      const normalized = newEmail.trim().toLowerCase();
      try {
        await api.changeEmail({ new_email: normalized });
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          const { email_verified } = await api.getEmailVerificationStatus();
          if (resolveChangeEmailConflict(email_verified) === 'self-heal') {
            // Verified in another tab — drop the stale banner, no error.
            await refreshEmailVerification();
            return;
          }
          throw new ChangeEmailTakenError(normalized);
        }
        // 400 (invalid/unchanged) / 429 (rate limit) / anything else —
        // surface unchanged for the form to render.
        throw err;
      }
      // emailVerified deliberately stays false: the round-trip for the
      // new address hasn't completed. The banner re-renders naming it.
      setUserEmail(normalized);
    },
    [refreshEmailVerification],
  );

  const logout = useCallback(() => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    expiryTimerRef.current = null;
    // F17.1 — tell every other tab to log out too. Done before the
    // local teardown so that even if React batching defers the
    // setStatus below, peers don't race ahead and post their own
    // logout back to us.
    sessionChannelRef.current?.postMessage({ type: 'logout' });
    // Fire-and-forget server-side revocation. api.logout() clears the
    // in-memory tokens regardless of the round-trip outcome.
    void api.logout();
    // `lock()` is async across the Worker boundary but the logout path
    // doesn't need to await it — the UI state transition below is
    // independent of the Worker-side lock, and the Worker posts a
    // lock event that arrives out-of-band if anyone still cares.
    if (agentRef.current) void agentRef.current.lock();
    setAgent(null);
    setAgentKind(null);
    setFingerprint(null);
    setIdentityPubkey(null);
    setUserEmail(null);
    setTokenExpiresAt(null);
    setRefreshTokenExpiresAt(null);
    setKycStatus(null);
    setEmailVerified(null);
    setStatus('unauthenticated');
    setAuthToken(null);
    setRefreshToken(null);
  }, []);

  // F12.D — inactivity-based terminal-expiry timer. Anchored on the
  // refresh-token TTL (24h, slides forward on every /auth/refresh
  // response), not on the access-token TTL (3min, rotates under the
  // user transparently). Access-token rotation is handled inside
  // api.ts: pre-send if within PROACTIVE_REFRESH_WINDOW_SECS of exp,
  // transparent 401→refresh→retry as the fallback. The timer here
  // exists so that a user who truly doesn't touch the app for 24h
  // sees the UI flip to 'expired' rather than silently click a stale
  // button and get a generic error. F17.1 made the idle-timer below
  // the load-bearing layer; this 24h timer is now defence-in-depth.
  useEffect(() => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    expiryTimerRef.current = null;

    if (!refreshTokenExpiresAt) return;

    const nowSecs = Math.floor(Date.now() / 1000);
    const expiresInMs = Math.max((refreshTokenExpiresAt - nowSecs) * 1000, 0);

    expiryTimerRef.current = setTimeout(() => {
      if (statusRef.current === 'authenticated') {
        setStatus('expired');
      }
    }, expiresInMs);

    return () => {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, [refreshTokenExpiresAt]);

  const isAuthenticated = status === 'authenticated';
  const isLoggedIn = status === 'authenticated' || status === 'locked';

  // COB-548 (GF-3) — short-circuit the entire subtree for a prohibited
  // viewer. Checked BEFORE the unreachable screen: a 451 on the bootstrap
  // `GET /version` is a refusal, and offering "Try again" for it would be
  // a lie. No retry action, because nothing the user can do here changes
  // the answer. `servicing` deliberately does NOT short-circuit — that
  // user keeps the whole app minus the acquisition surface.
  //
  // COB-586 — because this replaces the whole subtree, the accept page's
  // own error state never renders, so the invitee who followed a claim
  // link has to be told about the INVITATION here or not at all. The path
  // is read straight off `window.location` rather than through
  // `useLocation()` on purpose: this component builds the context value
  // every consumer subscribes to, so taking a route subscription here
  // would re-render the entire app on every navigation to serve a screen
  // that renders once and is sticky thereafter.
  if (geoTier === 'prohibited') {
    return <GeoRestricted invited={window.location.pathname === ACCEPT_PATH} />;
  }

  // F17.1 — short-circuit the entire SessionProvider subtree when the
  // backend is unreachable. Sits below all hook calls so the rules of
  // hooks aren't violated.
  if (bootstrapStatus === 'unreachable') {
    return <BootstrapUnreachable onRetry={handleBootstrapRetry} />;
  }

  return (
    <SessionContext.Provider
      value={{
        status,
        isAuthenticated,
        isLoggedIn,
        idleSecondsRemaining,
        agent,
        agentKind,
        fingerprint,
        identityPubkey,
        userEmail,
        kycStatus,
        emailVerified,
        offerGated,
        geoTier,
        residencyAttested,
        residencyJurisdiction,
        bitcoinNetwork,
        passcodePublicSalt,
        passcodeCheck,
        beginNewAccount,
        completeNewAccount,
        unlockAndVerify,
        restoreAndVerify,
        resetPasscodeWithSeed,
        loginWithWeb3Auth,
        completeWeb3AuthRegistration,
        completeWeb3AuthEmailRegistration,
        cancelWeb3AuthPending,
        loginWithHardware,
        reconnectHardwareAgent,
        refreshKycStatus,
        noteOfferGated,
        noteResidencyAttested,
        resendVerificationEmail,
        refreshEmailVerification,
        changeEmail,
        logout,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
